export function readBooleanLocalStorage(
  key: string,
  fallback: boolean
): boolean {
  if (typeof window == "undefined") return fallback;

  try {
    const fromLocal = localStorage.getItem(key);
    if (fromLocal == "1") return true;
    if (fromLocal == "0") return false;
  } catch {
    return fallback;
  }

  return fallback;
}

export function writeBooleanLocalStorage(key: string, value: boolean): void {
  if (typeof window == "undefined") return;
  const normalized = value ? "1" : "0";
  try {
    localStorage.setItem(key, normalized);
  } catch {
    // Ignore storage errors for private mode/quota.
  }
}

export function readNumberLocalStorage(
  key: string,
  fallback: number,
  validator?: (value: number) => boolean
): number {
  if (typeof window == "undefined") return fallback;

  const parse = (raw: string | null): number | undefined => {
    if (raw == null) return undefined;
    const value = Number(raw);
    if (!Number.isFinite(value)) return undefined;
    const normalized = Math.floor(value);
    if (validator && !validator(normalized)) return undefined;
    return normalized;
  };

  try {
    const fromLocal = parse(localStorage.getItem(key));
    if (fromLocal != undefined) return fromLocal;
  } catch {
    return fallback;
  }

  return fallback;
}

export function writeNumberLocalStorage(key: string, value: number): void {
  if (typeof window == "undefined") return;
  try {
    localStorage.setItem(key, String(Math.floor(value)));
  } catch {
    // Ignore storage errors for private mode/quota.
  }
}

export function readSessionStringStorage<T extends string>(
  key: string,
  fallback: T,
  validator: (value: string) => value is T
): T {
  if (typeof window == "undefined") return fallback;

  try {
    const raw = sessionStorage.getItem(key);
    if (raw == null) return fallback;
    if (validator(raw)) return raw;
    return fallback;
  } catch {
    return fallback;
  }
}

export function writeSessionStringStorage(key: string, value: string): void {
  if (typeof window == "undefined") return;
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Ignore storage errors for private mode/quota.
  }
}

export function readJsonLocalStorage<T>(key: string): T | undefined {
  if (typeof window == "undefined") return undefined;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return undefined;
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
}

export function writeJsonLocalStorage(key: string, value: unknown): void {
  if (typeof window == "undefined") return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage errors for private mode/quota.
  }
}
